import React from 'react';
import {browserHistory} from 'react-router';
import {ReactComponent as BackgroundIcon} from 'images/dashboard/exec-suggest.svg';
import WidgetBase from 'components/manager-dashboard/WidgetBase';
import {associateBy} from 'utils/associateBy';
import classNames from 'classnames';
import buildUrl from 'components/dashboard/buildUrl';
import './AdminSuggestedActions.styl';
import SuggestedActionDescription from 'components/dashboard/admin/SuggestedActionDescription';
import {HISTORY_ACTIONS} from 'components/StatsPage2.utils';

// This list is in priority order as defined in EV-13377
const SUGGESTED_ACTIONS = [
  {
    type: 'ROLL_OUT_COACHING_PLANS',
    title: 'Roll Out Coaching Plans',
    teamPossessive: true,
    description:
      params => ` team has ${params.performanceLabel} scores, and ${params.firstNameSubjective}
      doing minimal coaching. Meet with ${params.firstNameObjective} to roll out coaching plans to their team.`,
  },
  {
    type: 'FOCUS_ON_SKILL_DEVELOPMENT',
    title: 'Focus On Skill Development',
    teamPossessive: true,
    description: params => ` team is ${params.performanceLabel} on ${params.skill},
      and ${params.firstNameSubjectiveDo} not have any ${params.skill} coaching plans.
      Meet with ${params.firstNameObjective} to roll out ${params.skill} coaching plans to their team.`,
  },
  {
    type: 'USE_EXECVISION_FOR_COACHING',
    title: 'Use Coach For Coaching',
    description: params => ` did no coaching in the period.
      Meet with ${params.firstNameObjective} to discuss effective use of Coach.`,
  },
  {
    type: 'MONITOR_INBOX_FOR_FEEDBACK_REQUESTS',
    title: 'Monitor Inbox For Feedback Requests',
    description: params => ` is not responding to requests for feedback.
      Meet with ${params.firstNameObjective} and train them on using Coach to coach reps who request assistance.`,
  },
  {
    type: 'USE_AD_HOC_COACHING_PLANS',
    title: 'Use Ad Hoc Coaching Plans',
    description: params => ` has never used the Ad Hoc coaching plan feature.
      Meet with ${params.firstNameObjective} and show them how to effectively use on-the-spot coaching plans.`,
  },
  {
    type: 'REVIEW_MANAGERS_COACHING_STYLE',
    title: "Review Manager's Coaching Style",
    description: params => ` is not effectively improving rep performance during coaching plans.
      Review several of ${params.firstNamePossessive} prior coaching plans
      and offer suggestions on how they can tailor their feedback.`,
  },
  {
    type: 'NO_RECOMMENDATIONS',
    title: 'Great Job!',
    description: () =>
      ` is running an effective coaching program with their team, and reps are showing signs of improvement.`,
  },
];

export const suggestedActionsMap = associateBy(SUGGESTED_ACTIONS, it => it.type);

/**
 * take according to the order in SUGGESTED_ACTIONS, but take only one from each type,
 * only if there are not enough, then we take seconds from each type, if they are there, and so on.
 * We also try not to give two recommendations for the same manager, as much as we can
 */
export function takeTopN(recommendations, numActionsShown) {
  // first, add a unique id to each recommendation so we can track them
  // (just the index that they come in with)
  recommendations.forEach((r, idx) => {
    r.id = `${idx}`;
  });
  // in the first round we don't take more than one from each manager,
  // hence we create a set to track it
  const managerSeen = {};
  const round1 = oneRoundOfTakeTopN(recommendations, numActionsShown, managerSeen);
  if (round1.length >= numActionsShown) {
    return round1.slice(0, numActionsShown);
  }
  const alreadyHave = {};
  round1.forEach(r => {
    alreadyHave[r.id] = true;
  });
  const leftAfterRound1 = recommendations.filter(r => !alreadyHave[r.id]);
  // in the second round we can take repeats from one manager,
  // but of course we don't want anything we already have
  const round2 = oneRoundOfTakeTopN(leftAfterRound1, numActionsShown - round1.length, null);
  return round1.concat(round2).slice(0, numActionsShown);
}

function oneRoundOfTakeTopN(recommendations, numNeeded, managersSeen) {
  const recommendationsByType = arrangeByType(recommendations);
  let results = [];
  let i = 0;
  for (i; i < numNeeded; i++) {
    const batch = takeTheNthOfEachType(recommendationsByType, i, managersSeen);
    // if we are tracking managers seen, then it is possible that we skip a whole nth-layer
    // and still can take from the layer below, in such case we will just have to keep going,
    // in reality the numNeeded is just 3, so nbd
    if (!managersSeen && batch.length === 0) {
      break;
    }
    results = results.concat(batch);
    if (results.length >= numNeeded) {
      break;
    }
  }
  return results.slice(0, numNeeded);
}

/**
 * Given a list of recommendations, return a map keyed by recommendation type and the values are
 * the recommendations of that type, the order of the recommendations within each type is preserved
 */
function arrangeByType(recommendations) {
  let results = {};
  recommendations.forEach(it => {
    if (results[it.type] === undefined) {
      results[it.type] = [it];
    } else {
      results[it.type].push(it);
    }
  });
  return results;
}

/**
 * Does what the name says, return the taken ones, the recommendations are untouched.
 * However, if an item's manager is already in a selected suggested action, we will skip over it
 * @param nth take the nth of each type, zero-based
 * @param managersSeen an object with keys being the managers that have already had suggested actions chosen.
 * Can be null, if so, we will not track by managers
 */
function takeTheNthOfEachType(recommendationsByType, nth, managersSeen) {
  let results = [];
  SUGGESTED_ACTIONS.forEach(it => {
    const ofThisType = recommendationsByType[it.type];
    if (ofThisType && ofThisType.length > nth) {
      const candidate = ofThisType[nth];
      if (managersSeen) {
        if (!managersSeen[candidate.managerId]) {
          results.push(candidate);
          managersSeen[candidate.managerId] = true;
        }
      } else {
        // just add it, we are not tracking managers
        results.push(candidate);
      }
    }
  });
  return results;
}

export default class AdminSuggestedActions extends WidgetBase {
  constructor(props) {
    super(props);
    this.onClick = this.onClick.bind(this);
    this.renderAction = this.renderAction.bind(this);
  }

  onClick(managerId) {
    const path = buildUrl({
      adminId: this.props.userId,
      managerId,
    });
    const fromPath = this.props.fromPath;
    const state = fromPath
      ? {
          pathname: fromPath,
          action: HISTORY_ACTIONS.PUSH,
        }
      : {};
    const link = {
      pathname: path,
      state,
    };
    browserHistory.push(link);
  }

  renderAction(rec, index) {
    const template = suggestedActionsMap.get(rec.type);
    if (!template) {
      // this is actually not possible, because we only pick the ones we know in the first place
      console.error('Encountered unknown recommendation type', rec.type, rec);
      return null;
    }
    const typeClass = rec.type.toLowerCase();

    // TODO this could be a WavyCard
    return (
      <div className="suggested-action" key={index} onClick={() => this.onClick(rec.managerId)}>
        <div className="action-title">{template.title}</div>
        <div className="header">
          <BackgroundIcon className={classNames('background-icon', typeClass)} />
        </div>
        <div className="body">
          <SuggestedActionDescription rec={rec} template={template} />
        </div>
      </div>
    );
  }

  renderFiller(key) {
    return <div key={key} className="suggested-action filler" />;
  }

  render() {
    const {recommendations, numActionsShown} = this.props;
    const topN =
      recommendations && recommendations.length > 0
        ? takeTopN(recommendations, numActionsShown)
        : [];
    const title = 'Suggested Actions';
    const len = topN.length;
    return (
      <div className="standalone-widget-wrapper exec-suggested-actions">
        <div className="widget">
          <header className="widget-head">
            <div className="title-and-dates">
              <div className="widget-title">
                <h2>{title}</h2>
              </div>
            </div>
          </header>
          <div className="actions-list">
            {len === 0 ? (
              <div className="nothing-yet">
                We don't have any recommendations for you yet. As your team receives more scoring we
                will generate recommendations for you.
                <br />
                (Reps who already have coaching plans won't appear here.)
              </div>
            ) : (
              topN.map((rec, i) => this.renderAction(rec, i))
            )}
            {len > 0 &&
              len < numActionsShown &&
              [...Array(numActionsShown - len).keys()].map(i => this.renderFiller(i))}
          </div>
        </div>
      </div>
    );
  }
}
